import axios from "axios";
import { API_ENDPOINT } from "configs/api";
import LocalStorageService from "services/local-storage";

class HttpService {
  constructor() {
    this.pendingRequests = new Map(); // key: "METHOD:endpoint" → cancel function
  }

  // ✅ Key by METHOD + endpoint only (ignore params/user_profile_id)
  getRequestKey(method, endpoint) {
    return `${method}:${endpoint}`;
  }

  cancelDuplicate(requestKey) {
    if (this.pendingRequests.has(requestKey)) {
      const cancel = this.pendingRequests.get(requestKey);
      cancel("Cancelled: newer request for same endpoint");
      this.pendingRequests.delete(requestKey);
    }
  }

  removePending(requestKey, token) {
    // Only remove if it's still the same (latest) token for this key
    if (this.pendingRequests.get(requestKey) === token) {
      this.pendingRequests.delete(requestKey);
    }
  }

  async config(config, endpoint) {
    const rawUserData = LocalStorageService.get("user_data");
    let token = null;
    let userData = null;

    if (rawUserData.value) {
      userData = JSON.parse(rawUserData.value);
      token = userData.access_token;
    }

    if (!endpoint.includes("http://") && !endpoint.includes("https://"))
      config.baseURL = API_ENDPOINT;

    if (!config.headers) config.headers = {};

    if (
      token &&
      !config.headers.Authorization &&
      !endpoint.includes("http://") &&
      !endpoint.includes("https://")
    )
      config.headers.Authorization = `Bearer ${token}`;

    return config;
  }

  handleErrorResponse(errorData) {
    let throwbackData = errorData;

    if (axios.isCancel(errorData)) {
      console.log("Request cancelled:", errorData.message);
      return { cancelled: true, message: errorData.message };
    }

    if (errorData.response) {
      throwbackData = errorData.response;
      console.error(throwbackData);
      if (
        throwbackData.status === 401 &&
        (throwbackData.data.type === "TokenInvalidException" ||
          throwbackData.data.type === "TokenBlacklistedException")
      ) {
        let authErrorMessage =
          throwbackData.data?.message ||
          "Your session has expired. Please re-login to access your dashboard again.";
        LocalStorageService.remove("user_data");
        LocalStorageService.set("authentication_error", authErrorMessage);
        window.location.href = `${window.location.origin}/login`;
      }
    }

    return throwbackData;
  }

  handleResponse(response) {
    const headers = response.headers;
    if (headers["authorization"]) {
      const userdata = LocalStorageService.get("user_data");
      const parsedUserData = JSON.parse(userdata.value);
      const newData = {
        ...parsedUserData,
        access_token: headers["authorization"],
      };
      LocalStorageService.set("user_data", JSON.stringify(newData));
    }

    return response;
  }

  // ✅ New param: `dedupe` — set true to cancel previous in-flight requests to same endpoint
  get(endpoint, params, config = { headers: {} }, dedupe = false) {
    return new Promise(async (resolve, reject) => {
      let requestKey, cancelToken;

      if (dedupe) {
        requestKey = this.getRequestKey("GET", endpoint);
        this.cancelDuplicate(requestKey);

        cancelToken = new axios.CancelToken((cancel) => {
          this.pendingRequests.set(requestKey, cancel);
        });
      }

      config.params = params;
      config = await this.config(config, endpoint);
      if (dedupe) config.cancelToken = cancelToken;

      axios
        .get(endpoint, config)
        .then((response) => {
          if (dedupe) this.removePending(requestKey, cancelToken);
          resolve(this.handleResponse(response));
        })
        .catch((err) => {
          if (dedupe) this.removePending(requestKey, cancelToken);
          reject(this.handleErrorResponse(err));
        });
    });
  }

  post(endpoint, params, config = { headers: {} }, dedupe = false) {
    return new Promise(async (resolve, reject) => {
      let requestKey, cancelToken;

      if (dedupe) {
        requestKey = this.getRequestKey("POST", endpoint);
        this.cancelDuplicate(requestKey);

        cancelToken = new axios.CancelToken((cancel) => {
          this.pendingRequests.set(requestKey, cancel);
        });
      }

      config = await this.config(config, endpoint);
      if (dedupe) config.cancelToken = cancelToken;

      axios
        .post(endpoint, params, config)
        .then((response) => {
          if (dedupe) this.removePending(requestKey, cancelToken);
          resolve(this.handleResponse(response));
        })
        .catch((err) => {
          if (dedupe) this.removePending(requestKey, cancelToken);
          reject(this.handleErrorResponse(err));
        });
    });
  }

  put(endpoint, params, config = { headers: {} }, dedupe = false) {
    return new Promise(async (resolve, reject) => {
      let requestKey, cancelToken;

      if (dedupe) {
        requestKey = this.getRequestKey("PUT", endpoint);
        this.cancelDuplicate(requestKey);

        cancelToken = new axios.CancelToken((cancel) => {
          this.pendingRequests.set(requestKey, cancel);
        });
      }

      config = await this.config(config, endpoint);
      if (dedupe) config.cancelToken = cancelToken;

      axios
        .put(endpoint, params, config)
        .then((response) => {
          if (dedupe) this.removePending(requestKey, cancelToken);
          resolve(this.handleResponse(response));
        })
        .catch((err) => {
          if (dedupe) this.removePending(requestKey, cancelToken);
          reject(this.handleErrorResponse(err));
        });
    });
  }

  patch(endpoint, params, config = { headers: {} }, dedupe = false) {
    return new Promise(async (resolve, reject) => {
      let requestKey, cancelToken;

      if (dedupe) {
        requestKey = this.getRequestKey("PATCH", endpoint);
        this.cancelDuplicate(requestKey);

        cancelToken = new axios.CancelToken((cancel) => {
          this.pendingRequests.set(requestKey, cancel);
        });
      }

      config = await this.config(config, endpoint);
      if (dedupe) config.cancelToken = cancelToken;

      axios
        .patch(endpoint, params, config)
        .then((response) => {
          if (dedupe) this.removePending(requestKey, cancelToken);
          resolve(this.handleResponse(response));
        })
        .catch((err) => {
          if (dedupe) this.removePending(requestKey, cancelToken);
          reject(this.handleErrorResponse(err));
        });
    });
  }

  delete(endpoint, params, config = { headers: {} }, dedupe = false) {
    return new Promise(async (resolve, reject) => {
      let requestKey, cancelToken;

      if (dedupe) {
        requestKey = this.getRequestKey("DELETE", endpoint);
        this.cancelDuplicate(requestKey);

        cancelToken = new axios.CancelToken((cancel) => {
          this.pendingRequests.set(requestKey, cancel);
        });
      }

      config.params = params;
      config = await this.config(config, endpoint);
      if (dedupe) config.cancelToken = cancelToken;

      axios
        .delete(endpoint, config)
        .then((response) => {
          if (dedupe) this.removePending(requestKey, cancelToken);
          resolve(this.handleResponse(response));
        })
        .catch((err) => {
          if (dedupe) this.removePending(requestKey, cancelToken);
          reject(this.handleErrorResponse(err));
        });
    });
  }
}

const HTTPServices = new HttpService();
export default HTTPServices;
